import { CvArtisteTab } from './CvArtisteTab.js';
import { DossierArtisteDialog } from './DossierArtisteDialog.js';
import { CatalogageBloc } from '../catalogage/CatalogageBloc.js';
import { AttenteArtiste } from '../pipeline/ListeAttente.js';
import { CompteArtisteSection } from './CompteArtisteSection.js';
import { IndicateurFraicheur } from '../racine/IndicateurFraicheur.js';
import { SectionPresse } from '../presse/SectionPresse.js';
import { ContratRepresentationSection } from './ContratRepresentationSection.js';
import { useApercuMedia, useDeposerMedia } from '../../api/medias.js';
import {
  Tabs,
  Badge,
  Button,
  Checkbox,
  classesBouton,
  Dialog,
  Input,
  InventoryNumber,
  Textarea,
} from '@artosera/ui';
import { GripVertical } from 'lucide-react';
import type { DragEvent, JSX } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { useOeuvres, type OeuvrePresentee } from '../../api/oeuvres.js';
import { estApiError } from '../../api/errors.js';
import {
  useArchiverArtiste,
  useArtiste,
  useModifierArtiste,
  useOrdonnerOeuvresArtiste,
  useRestaurerArtiste,
  type ArtistePresente,
} from '../../api/artistes.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import type { GalerieSession } from '../../api/types.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { RetourGeste } from '../RetourGeste.js';
import { GardeSaisie } from '../GardeSaisie.js';
import { useErreurFormulaire } from '../useErreurFormulaire.js';
import { LIMITES } from '../validations.js';
import { PastilleStatut } from '../../composants/PastilleStatut.js';
import { VignetteOeuvre } from '../../composants/VignetteOeuvre.js';
import { Monogramme } from '../../composants/Monogramme.js';
import styles from './ArtisteDetailPage.module.css';

/** Fiche artiste (3.3, point 4) : biographie FR/EN, représentation, œuvres ordonnables. */
export function ArtisteDetailPage(): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const { id } = useParams<{ id: string }>();
  const { galerieSlug, galerie } = useShellContext();

  const artiste = useArtiste(galerieSlug, id ?? null);
  const oeuvres = useOeuvres(galerieSlug, {
    artisteId: id,
    parPage: 200,
    inclureArchivees: true,
    vignettes: true,
  });

  if (artiste.isLoading) return <p className={styles.vide}>{t('commun:chargement')}</p>;
  if (!artiste.data) {
    return (
      <p className={styles.vide} role="alert">
        {estApiError(artiste.error) ? artiste.error.message : t('commun:erreurReseau')}
      </p>
    );
  }

  return (
    <FicheArtiste
      // Remonte à chaque changement d'artiste : le formulaire part des
      // vraies valeurs sans effet de synchronisation (React 19 refuse le
      // `setState` synchrone dans un effet pour ce cas).
      key={artiste.data.id}
      galerieSlug={galerieSlug}
      galerie={galerie}
      depuis={artiste.dataUpdatedAt}
      donnees={artiste.data}
      oeuvres={oeuvres.data?.oeuvres ?? []}
      vignettes={oeuvres.data?.vignettes?.urls ?? {}}
    />
  );
}

interface FicheArtisteProps {
  depuis: number;
  galerieSlug: string;
  galerie: GalerieSession;
  donnees: ArtistePresente;
  oeuvres: readonly OeuvrePresentee[];
  vignettes: Readonly<Record<string, string>>;
}

function FicheArtiste({
  depuis,
  galerieSlug,
  galerie,
  donnees,
  oeuvres,
  vignettes,
}: FicheArtisteProps): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const navigate = useNavigate();
  const portrait = useApercuMedia(galerieSlug, donnees.portraitId);

  const modifier = useModifierArtiste(galerieSlug);
  const deposerPortrait = useDeposerMedia(galerieSlug);
  const portraitRef = useRef<HTMLInputElement>(null);
  const archiver = useArchiverArtiste(galerieSlug);
  const restaurer = useRestaurerArtiste(galerieSlug);
  const ordonner = useOrdonnerOeuvresArtiste(galerieSlug);

  const [dossierOuvert, setDossierOuvert] = useState(false);
  const [nom, setNom] = useState(donnees.nom);
  const [naissance, setNaissance] = useState(donnees.naissance ?? '');
  const [nationalite, setNationalite] = useState(donnees.nationalite ?? '');
  const [biographieFr, setBiographieFr] = useState(donnees.biographie?.fr ?? '');
  const [biographieEn, setBiographieEn] = useState(donnees.biographie?.en ?? '');
  const [visibleSurLeSite, setVisibleSurLeSite] = useState(donnees.visibleSurLeSite);
  const [representee, setRepresentee] = useState<boolean | null>(null);
  const formulaire = useErreurFormulaire();
  const [succes, setSucces] = useState(false);
  // Garde de saisie : écart avec la dernière version enregistrée.
  const saisie = JSON.stringify([
    nom,
    naissance,
    nationalite,
    biographieFr,
    biographieEn,
    visibleSurLeSite,
    representee,
  ]);
  const [saisieEnregistree, setSaisieEnregistree] = useState(saisie);
  const modifie = saisie !== saisieEnregistree;
  const [archivageOuvert, setArchivageOuvert] = useState(false);
  const [retour, setRetour] = useState<string | null>(null);
  const [glisseId, setGlisseId] = useState<string | null>(null);

  const peutEcrire = galerieALaction(galerie, 'artists:write');
  // Module coupé, le cœur reprend la tenue manuelle du résumé (M5).
  const representationContractuelle =
    !!donnees.representation.contratId && galerieAModule(galerie, 'contrats-artistes');

  const oeuvresTriees = [...oeuvres].sort((a, b) => {
    const ordreA = donnees.ordreOeuvres.indexOf(a.id);
    const ordreB = donnees.ordreOeuvres.indexOf(b.id);
    if (ordreA === -1 && ordreB === -1) return 0;
    if (ordreA === -1) return 1;
    if (ordreB === -1) return -1;
    return ordreA - ordreB;
  });

  function deplacerOeuvre(cibleId: string): void {
    if (!glisseId || glisseId === cibleId) return;
    const ids = oeuvresTriees.map((oeuvre) => oeuvre.id);
    const depart = ids.indexOf(glisseId);
    const arrivee = ids.indexOf(cibleId);
    if (depart === -1 || arrivee === -1) return;
    ids.splice(arrivee, 0, ...ids.splice(depart, 1));
    ordonner.mutate({ id: donnees.id, oeuvres: ids });
  }

  // Le code pays est ISO 3166-1 alpha-2 (« FR », « US ») : deux lettres.
  const nationaliteValide = nationalite.trim() === '' || /^[A-Za-z]{2}$/.test(nationalite.trim());
  const identiteValide = nom.trim().length > 0 && nationaliteValide;

  async function choisirPortrait(fichier: File): Promise<void> {
    formulaire.reinitialiser();
    try {
      const depose = await deposerPortrait.mutateAsync({
        fichier,
        rattachementType: 'artist',
        rattachementId: donnees.id,
        type: 'portrait',
      });
      await modifier.mutateAsync({ id: donnees.id, corps: { portraitId: depose.media.id } });
      setSucces(true);
    } catch (error) {
      formulaire.appliquer(error);
    }
  }

  async function enregistrer(): Promise<void> {
    if (!identiteValide) return;
    formulaire.reinitialiser();
    setSucces(false);
    try {
      await modifier.mutateAsync({
        id: donnees.id,
        corps: {
          nom: nom.trim(),
          naissance: naissance.trim(),
          ...(nationalite.trim() ? { nationalite: nationalite.trim().toUpperCase() } : {}),
          biographie: { fr: biographieFr, ...(biographieEn ? { en: biographieEn } : {}) },
          visibleSurLeSite,
          ...(representationContractuelle || representee === null
            ? {}
            : { representation: { representee } }),
        },
      });
      setRepresentee(null);
      setSaisieEnregistree(
        JSON.stringify([
          nom,
          naissance,
          nationalite,
          biographieFr,
          biographieEn,
          visibleSurLeSite,
          null,
        ]),
      );
      setSucces(true);
    } catch (error) {
      formulaire.appliquer(error, ['nom', 'naissance', 'nationalite', 'biographie']);
    }
  }

  return (
    <div className={styles.page}>
      <GardeSaisie actif={modifie && peutEcrire} />
      <CatalogageBloc
        artiste={donnees}
        onBiographieAcceptee={(valeur) => {
          setBiographieFr(valeur.fr);
          setBiographieEn(valeur.en);
          setSaisieEnregistree((avant) => {
            const champs = JSON.parse(avant) as unknown[];
            champs[3] = valeur.fr;
            champs[4] = valeur.en;
            return JSON.stringify(champs);
          });
        }}
      />
      <Link to={`/${galerieSlug}/artistes`} className={styles.retour}>
        ← {t('regie:artistes.retour')}
      </Link>

      {galerieAModule(galerie, 'pipeline-ventes') && galerieALaction(galerie, 'pipeline:write') ? (
        <AttenteArtiste artisteId={donnees.id} />
      ) : null}
      <header className={styles.entete}>
        <Monogramme
          nom={donnees.nom}
          portraitUrl={portrait.data ?? null}
          taille={88}
          forme="carre"
        />
        <div className={styles.identite}>
          <h1 className={styles.titre}>{donnees.nom}</h1>
          <IndicateurFraicheur depuis={depuis} />
          {donnees.naissance || donnees.nationalite ? (
            <p className={styles.etatCivil}>
              {[donnees.naissance, donnees.nationalite].filter(Boolean).join(' · ')}
            </p>
          ) : null}
          <div className={styles.badges}>
            <Badge tone={donnees.representation.representee ? 'accent' : 'neutral'}>
              {donnees.representation.representee
                ? t('regie:artistes.representee')
                : t('regie:artistes.nonRepresentee')}
            </Badge>
            {donnees.representation.representee && donnees.representation.exclusivite ? (
              <Badge tone="neutral">{t('regie:artistes.representationExclusivite')}</Badge>
            ) : null}
            {donnees.archive ? <Badge tone="withdrawn">{t('regie:artistes.archive')}</Badge> : null}
          </div>
        </div>
        {peutEcrire && donnees.archive ? (
          <Button
            className={styles.archiver}
            variant="secondary"
            size="sm"
            loading={restaurer.isPending}
            onClick={() =>
              restaurer.mutate(donnees.id, {
                onSuccess: () => setRetour(t('regie:artistes.restaure')),
              })
            }
          >
            {t('regie:artistes.restaurer')}
          </Button>
        ) : peutEcrire ? (
          <Button
            className={styles.archiver}
            variant="danger"
            size="sm"
            onClick={() => setArchivageOuvert(true)}
          >
            {t('regie:artistes.archiver')}
          </Button>
        ) : null}
        {galerieAModule(galerie, 'modeles-contrats') &&
          galerieALaction(galerie, 'contracts:write') && (
            <Link
              className={classesBouton('secondary', 'md', styles.lienContrat)}
              to={`/${galerieSlug}/contrats?nouveau=1&genre=artiste&artisteId=${donnees.id}`}
            >
              {t('regie:artistes.genererContrat')}
            </Link>
          )}
        {galerieAModule(galerie, 'documents-prestige') &&
          galerieALaction(galerie, 'artists:read') && (
            <Button variant="secondary" onClick={() => setDossierOuvert(true)}>
              {t('regie:dossierArtiste.titre')}
            </Button>
          )}
      </header>
      <RetourGeste message={retour} />
      {restaurer.isError ? (
        <p className={styles.erreur} role="alert">
          {estApiError(restaurer.error) ? restaurer.error.message : t('commun:erreurReseau')}
        </p>
      ) : null}

      <div className={styles.corps}>
        <Tabs
          tabs={[
            {
              value: 'biographie',
              label: t('regie:artistes.onglets.biographie'),
              content: (
                <section className={styles.panneau} aria-labelledby="artiste-biographie-titre">
                  <h2 id="artiste-biographie-titre" className={styles.sousTitre}>
                    {t('regie:artistes.onglets.biographie')}
                  </h2>
                  {peutEcrire ? (
                    <div className={styles.identiteChamps}>
                      <Field
                        label={t('regie:artistes.nomChamp')}
                        htmlFor="artiste-nom-fiche"
                        error={
                          nom.trim().length === 0
                            ? t('regie:artistes.nomObligatoire')
                            : formulaire.messageChamp('nom')
                        }
                      >
                        <Input
                          id="artiste-nom-fiche"
                          maxLength={LIMITES.artiste.nom}
                          value={nom}
                          onChange={(event) => setNom(event.target.value)}
                        />
                      </Field>
                      <Field
                        label={t('regie:artistes.naissanceChamp')}
                        htmlFor="artiste-naissance"
                        error={formulaire.messageChamp('naissance')}
                      >
                        <Input
                          id="artiste-naissance"
                          maxLength={LIMITES.artiste.naissance}
                          placeholder={t('regie:artistes.naissanceExemple')}
                          value={naissance}
                          onChange={(event) => setNaissance(event.target.value)}
                        />
                      </Field>
                      <Field
                        label={t('regie:artistes.nationaliteChamp')}
                        htmlFor="artiste-nationalite"
                        error={
                          nationaliteValide
                            ? formulaire.messageChamp('nationalite')
                            : t('regie:artistes.nationaliteInvalide')
                        }
                      >
                        <Input
                          id="artiste-nationalite"
                          maxLength={2}
                          placeholder="FR"
                          value={nationalite}
                          onChange={(event) => setNationalite(event.target.value.toUpperCase())}
                        />
                      </Field>
                      <div className={styles.portraitChamp}>
                        <span className={styles.portraitLibelle}>
                          {t('regie:artistes.portrait')}
                        </span>
                        <Button
                          variant="secondary"
                          size="sm"
                          loading={deposerPortrait.isPending}
                          onClick={() => portraitRef.current?.click()}
                        >
                          {donnees.portraitId
                            ? t('regie:artistes.portraitChanger')
                            : t('regie:artistes.portraitDeposer')}
                        </Button>
                        <input
                          ref={portraitRef}
                          type="file"
                          accept="image/*"
                          hidden
                          aria-label={t('regie:artistes.portraitDeposer')}
                          onChange={(event) => {
                            const fichier = event.target.files?.[0];
                            if (fichier) void choisirPortrait(fichier);
                            event.target.value = '';
                          }}
                        />
                      </div>
                    </div>
                  ) : null}
                  <div className={styles.biographies}>
                    <Field label={t('regie:artistes.biographieFr')} htmlFor="artiste-bio-fr">
                      <Textarea
                        id="artiste-bio-fr"
                        rows={6}
                        value={biographieFr}
                        onChange={(event) => setBiographieFr(event.target.value)}
                      />
                    </Field>
                    <Field label={t('regie:artistes.biographieEn')} htmlFor="artiste-bio-en">
                      <Textarea
                        id="artiste-bio-en"
                        rows={6}
                        value={biographieEn}
                        onChange={(event) => setBiographieEn(event.target.value)}
                      />
                    </Field>
                  </div>

                  <div className={styles.cases}>
                    <Checkbox
                      id="artiste-visible"
                      label={t('regie:artistes.visibleSurLeSite')}
                      checked={visibleSurLeSite}
                      onCheckedChange={(valeur) => setVisibleSurLeSite(valeur === true)}
                    />
                    <Checkbox
                      id="artiste-representee"
                      disabled={representationContractuelle}
                      label={t('regie:artistes.representee')}
                      checked={
                        representationContractuelle
                          ? donnees.representation.representee
                          : (representee ?? donnees.representation.representee)
                      }
                      onCheckedChange={(valeur) => setRepresentee(valeur === true)}
                    />
                  </div>

                  {formulaire.erreur ? (
                    <p className={styles.erreur} role="alert">
                      {formulaire.erreur}
                    </p>
                  ) : null}
                  {succes ? (
                    <p className={styles.succes} role="status">
                      {t('regie:artistes.enregistre')}
                    </p>
                  ) : null}

                  <div className={styles.actions}>
                    <Button
                      onClick={() => void enregistrer()}
                      loading={modifier.isPending}
                      disabled={!identiteValide}
                    >
                      {t('regie:artistes.enregistrer')}
                    </Button>
                  </div>
                </section>
              ),
            },
            ...(galerieAModule(galerie, 'documents-prestige') &&
            galerieALaction(galerie, 'artists:read')
              ? [
                  {
                    value: 'cv',
                    label: t('regie:cvArtiste.titre'),
                    content: (
                      <CvArtisteTab
                        galerieSlug={galerieSlug}
                        artisteId={donnees.id}
                        peutEcrire={peutEcrire}
                      />
                    ),
                  },
                ]
              : []),
          ]}
        />

        <CompteArtisteSection
          key={donnees.id}
          galerieSlug={galerieSlug}
          artisteId={donnees.id}
          galerie={galerie}
        />
        {galerieAModule(galerie, 'contrats-artistes') &&
          galerieALaction(galerie, 'artist-contracts:read') && (
            <ContratRepresentationSection
              galerieSlug={galerieSlug}
              artisteId={donnees.id}
              peutEcrire={galerieALaction(galerie, 'artist-contracts:write')}
              avecCoffre={
                galerieAModule(galerie, 'coffre') && galerieALaction(galerie, 'documents:read')
              }
              avecSignature={galerieAModule(galerie, 'signature-electronique')}
              avecModeles={
                galerieAModule(galerie, 'modeles-contrats') &&
                galerieALaction(galerie, 'contracts:read')
              }
            />
          )}
        <section className={styles.panneau} aria-labelledby="artiste-oeuvres-titre">
          <div className={styles.panneauEntete}>
            <h2 id="artiste-oeuvres-titre" className={styles.sousTitre}>
              {t('regie:artistes.onglets.oeuvres')}
            </h2>
            <span className={styles.compte}>{oeuvresTriees.length}</span>
          </div>
          <p className={styles.aide}>{t('regie:artistes.ordreOeuvresAide')}</p>
          {oeuvresTriees.length === 0 ? (
            <p className={styles.vide}>{t('regie:artistes.aucuneOeuvre')}</p>
          ) : (
            <ol className={styles.listeOeuvres}>
              {oeuvresTriees.map((oeuvre) => (
                <li
                  key={oeuvre.id}
                  className={styles.ligneOeuvre}
                  data-glisse={glisseId === oeuvre.id ? 'true' : undefined}
                  draggable
                  onDragStart={() => setGlisseId(oeuvre.id)}
                  onDragEnd={() => setGlisseId(null)}
                  onDragOver={(event: DragEvent) => event.preventDefault()}
                  onDrop={() => deplacerOeuvre(oeuvre.id)}
                >
                  <GripVertical
                    className={styles.poignee}
                    size={16}
                    strokeWidth={1.5}
                    aria-hidden
                  />
                  <VignetteOeuvre url={vignettes[oeuvre.id] ?? null} taille="md" />
                  <span className={styles.cartelOeuvre}>
                    <Link className={styles.lienOeuvre} to={`/${galerieSlug}/oeuvres/${oeuvre.id}`}>
                      {oeuvre.titre}
                    </Link>
                    <InventoryNumber value={oeuvre.numeroInventaire} />
                  </span>
                  <PastilleStatut className={styles.statutOeuvre} statut={oeuvre.statut} />
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>

      {galerieAModule(galerie, 'agenda-presse') && galerieALaction(galerie, 'press:read') ? (
        <SectionPresse galerieSlug={galerieSlug} filtre={{ artisteId: donnees.id }} />
      ) : null}
      {dossierOuvert && (
        <DossierArtisteDialog
          galerieSlug={galerieSlug}
          artisteId={donnees.id}
          fermer={() => setDossierOuvert(false)}
        />
      )}
      <Dialog
        open={archivageOuvert}
        onOpenChange={setArchivageOuvert}
        title={t('regie:artistes.archiverConfirmationTitre')}
        description={t('regie:artistes.archiverConfirmationTexte')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setArchivageOuvert(false)}>
              {t('commun:actions.annuler')}
            </Button>
            <Button
              variant="danger"
              loading={archiver.isPending}
              onClick={() => {
                archiver.mutate(donnees.id, {
                  onSuccess: () => {
                    setArchivageOuvert(false);
                    navigate(`/${galerieSlug}/artistes`);
                  },
                });
              }}
            >
              {t('regie:artistes.archiver')}
            </Button>
          </>
        }
      >
        <p>{donnees.nom}</p>
      </Dialog>
    </div>
  );
}
